import type { DataTableColumn } from '@/components/UI/DataTable/DataTable.model';
import type { SelectOption } from '@/components/UI/Select/Select';
import { sortArrAlphabetically } from '@/helpers';
import { _isNil, _notNil } from '@/littledash';
import type { TeamGlossaryApiId } from '@/model/Glosary.model';
import type { PresetCalculation } from '@/model/PresetCalculation.model';
import type { Validate } from 'react-hook-form';

type ObservationOption = { title?: string; api_id?: TeamGlossaryApiId };

export const valueIsNumeric: Validate<unknown, object> = (value: unknown) => {
  if (_isNil(value) || String(value ?? '').trim().length === 0) {
    return 'This field must be numeric';
  }
  return Number.isFinite(Number(value)) ? true : 'This field must be numeric & finite';
};
export const uniqueNameValidatorFactory =
  (names: Set<string>): Validate<string | null | undefined, object> =>
  (value?: string | null) =>
    names.has((value ?? '').trim()) ? 'Field must be unique' : true;

export const convertMetricsToOptions = (
  columns: Readonly<Array<DataTableColumn>>,
  refDate: string,
  metrics?: Array<PresetCalculation>
): Array<SelectOption<string>> =>
  (metrics ?? []).reduce(
    (acc, { id: value, name, unit }) => {
      const alreadyDisplayed = columns?.find(
        (col) => col.type === 'measurement' && col.measurement.metric === value && col.reference_date === refDate
      );
      if (_isNil(alreadyDisplayed)) {
        acc.push({
          value,
          label: name + (_isNil(unit) ? '' : ` (${unit})`),
        });
      }
      return acc;
    },
    [] as Array<SelectOption<string>>
  );

export const convertObservationsToOptions = (observations?: Array<ObservationOption>): Array<SelectOption<string>> => {
  const alphabeticalObservations: Array<ObservationOption> = _notNil(observations)
    ? sortArrAlphabetically(observations)
    : [];
  return alphabeticalObservations.reduce<Array<SelectOption<string>>>((acc, observation) => {
    if (_notNil(observation?.api_id)) {
      acc.push({
        label: observation?.title ?? '',
        value: observation.api_id,
      });
    }
    return acc;
  }, []);
};
